import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/Crud";
import LanguageSelector from "../components/LanguageSelector";
import { usePrefs, useFormat } from "../contexts/PrefsContext";
import { REGIONS, TIMEZONES, DATE_FORMATS, TIME_FORMATS, NUMBER_FORMATS, WEEK_STARTS, findRegion } from "../i18n/regions";
import { CURRENCIES } from "../i18n/currencies";
import { Globe, MapPin, CircleDollarSign, Sparkles, FileText, Check, ShieldCheck, Calculator } from "lucide-react";
import { useState } from "react";
import AppLockSettings from "../components/AppLockSettings";
import { useTaxCountries } from "../lib/useTaxCountries";

export default function Settings() {
    const { t } = useTranslation();
    const { prefs, updatePrefs } = usePrefs();
    const fmt = useFormat();
    const [savedKey, setSavedKey] = useState(0);

    const set = (patch) => { updatePrefs(patch); setSavedKey(k => k + 1); };
    const { countries: taxCountries } = useTaxCountries();

    const onRegionChange = (code) => {
        // Adopt the region's defaults for currency/timezone unless the user
        // has explicitly set them. We just patch — they can override per-field.
        const r = findRegion(code);
        set({ region: code, currency: r.currency, timezone: r.timezone });
    };

    return (
        <div data-testid="settings-page">
            <PageHeader title={t("settings.title")} subtitle={t("settings.subtitle")} />

            <Section icon={Globe} title={t("settings.languageHeading")} help={t("settings.languageHelp")} testid="settings-language">
                <LanguageSelector testid="settings-language-selector" />
            </Section>

            <Section icon={MapPin} title={t("settings.regionHeading")} help={t("settings.regionHelp")} testid="settings-region">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={t("settings.country")}>
                        <select value={prefs.region} onChange={(e) => onRegionChange(e.target.value)}
                                data-testid="settings-region-select"
                                className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                            {REGIONS.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
                        </select>
                    </Field>
                    <Field label={t("settings.timezone")}>
                        <select value={prefs.timezone || ""} onChange={(e) => set({ timezone: e.target.value })}
                                data-testid="settings-timezone-select"
                                className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                            {prefs.timezone && !TIMEZONES.includes(prefs.timezone) && <option value={prefs.timezone}>{prefs.timezone}</option>}
                            {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                    </Field>
                    <Field label={t("settings.dateFormat")}>
                        <select value={prefs.date_format} onChange={(e) => set({ date_format: e.target.value })}
                                data-testid="settings-date-format-select"
                                className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                            {DATE_FORMATS.map(d => <option key={d.code} value={d.code}>{d.label}</option>)}
                        </select>
                    </Field>
                    <Field label={t("settings.timeFormat")}>
                        <select value={prefs.time_format} onChange={(e) => set({ time_format: e.target.value })}
                                data-testid="settings-time-format-select"
                                className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                            {TIME_FORMATS.map(f => <option key={f.code} value={f.code}>{f.label}</option>)}
                        </select>
                    </Field>
                    <Field label={t("settings.numberFormat")}>
                        <select value={prefs.number_format} onChange={(e) => set({ number_format: e.target.value })}
                                data-testid="settings-number-format-select"
                                className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                            {NUMBER_FORMATS.map(f => <option key={f.code} value={f.code}>{f.label}</option>)}
                        </select>
                    </Field>
                    <Field label={t("settings.weekStart")}>
                        <select value={prefs.week_start ?? 1} onChange={(e) => set({ week_start: Number(e.target.value) })}
                                data-testid="settings-week-start-select"
                                className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                            {WEEK_STARTS.map(w => <option key={w.code} value={w.code}>{w.label}</option>)}
                        </select>
                    </Field>
                </div>
            </Section>

            <Section icon={CircleDollarSign} title={t("settings.currencyHeading")} help={t("settings.currencyHelp")} testid="settings-currency">
                <Field label={t("settings.currency")}>
                    <select value={prefs.currency} onChange={(e) => set({ currency: e.target.value })}
                            data-testid="settings-currency-select"
                            className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60 max-w-md">
                        {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name} ({c.symbol})</option>)}
                    </select>
                </Field>
            </Section>

            <Section icon={Sparkles} title={t("settings.aiHeading")} help={t("settings.aiHelp")} testid="settings-ai">
                <Field label="">
                    <select value={prefs.ai_language || ""} onChange={(e) => set({ ai_language: e.target.value })}
                            data-testid="settings-ai-language-select"
                            className="w-full max-w-md px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                        <option value="">Match interface language ({prefs.language || "en"})</option>
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        <option value="fr">Français</option>
                        <option value="de">Deutsch</option>
                        <option value="it">Italiano</option>
                        <option value="pt">Português</option>
                        <option value="nl">Nederlands</option>
                        <option value="pl">Polski</option>
                        <option value="ja">日本語</option>
                        <option value="ko">한국어</option>
                        <option value="zh-CN">简体中文</option>
                        <option value="ar">العربية</option>
                    </select>
                </Field>
            </Section>

            <Section icon={FileText} title={t("settings.documentsHeading")} help={t("settings.documentsHelp")} testid="settings-documents">
                <div className="grid sm:grid-cols-2 gap-2 max-w-2xl">
                    {[
                        ["keep",    t("settings.docKeep")],
                        ["full",    t("settings.docFull")],
                        ["summary", t("settings.docSummary")],
                        ["ai",      t("settings.docAi")],
                    ].map(([code, label]) => {
                        const active = (prefs.document_translation || "keep") === code;
                        return (
                            <button key={code} type="button" onClick={() => set({ document_translation: code })}
                                    data-testid={`settings-doc-${code}`}
                                    aria-pressed={active}
                                    className={`text-left text-sm px-3 py-2.5 rounded-xl border transition ${active ? "border-[var(--brand-purple)] bg-[var(--brand-purple)]/10" : "border-[rgba(26,19,24,0.12)] hover:border-[rgba(26,19,24,0.3)]"}`}>
                                <div className="flex items-center gap-2">
                                    {active ? <Check size={14} className="text-[var(--brand-purple)]" /> : <span className="w-3.5" />}
                                    {label}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </Section>

            <Section icon={Calculator} title="Finance · Tax Country" help="Used for the personal-finance tax estimate. This is an estimate only and should not be considered financial or tax advice. Not used for invoices, subscriptions, or billing." testid="settings-tax-country">
                <label className="text-sm block max-w-md">
                    <span className="text-[var(--text-ink)]/70">Country used for tax estimates</span>
                    <select value={prefs.tax_country || prefs.region || ""}
                            onChange={(e) => set({ tax_country: e.target.value })}
                            data-testid="settings-tax-country-select"
                            className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                        <option value="">Not set (uses region default)</option>
                        {taxCountries.map((c) => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                    </select>
                </label>
            </Section>

            <Section icon={ShieldCheck} title="Security · App Lock" help="Optional on-device lock. Your session stays signed in — App Lock adds a PIN or biometric gate when you reopen the app." testid="settings-app-lock">
                <AppLockSettings />
            </Section>

            <div className="card-warm p-5 mb-6" data-testid="settings-preview">
                <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55 mb-3">{t("settings.previewHeading")}</div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <PreviewRow label={t("settings.previewDate")}   value={fmt.fmtDate(new Date())} />
                    <PreviewRow label={t("settings.previewTime")}   value={fmt.fmtTime(new Date())} />
                    <PreviewRow label={t("settings.previewNumber")} value={fmt.fmtNumber(1234567.89)} />
                    <PreviewRow label={t("settings.previewAmount")} value={fmt.fmtCurrency(1234.5)} />
                </div>
            </div>

            {savedKey > 0 && (
                <div key={savedKey} className="fixed left-1/2 -translate-x-1/2 brand-gradient text-white text-sm px-4 py-2 rounded-full shadow-lg animate-pulse" style={{ bottom: "max(1.5rem, env(safe-area-inset-bottom) + 0.5rem)" }} data-testid="settings-saved-toast">
                    {t("settings.savedToast")}
                </div>
            )}
        </div>
    );
}

function Section({ icon: Icon, title, help, children, testid }) {
    return (
        <section className="card-warm p-5 mb-5" data-testid={testid}>
            <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl brand-gradient grid place-items-center text-white shrink-0"><Icon size={16} /></div>
                <div>
                    <h2 className="font-serif text-xl leading-tight">{title}</h2>
                    {help && <p className="text-xs text-[var(--text-ink)]/65 mt-0.5 leading-snug">{help}</p>}
                </div>
            </div>
            {children}
        </section>
    );
}

function Field({ label, children }) {
    return (
        <label className="text-sm block">
            {label && <span className="text-[var(--text-ink)]/70">{label}</span>}
            <div className={label ? "mt-1" : ""}>{children}</div>
        </label>
    );
}

function PreviewRow({ label, value }) {
    return (
        <div className="flex items-baseline justify-between gap-3 px-3 py-2 rounded-lg bg-[rgba(26,19,24,0.04)]">
            <span className="text-[var(--text-ink)]/65">{label}</span>
            <span className="font-mono tabular-nums" data-testid={`preview-${label.replace(/\s+/g, "-").toLowerCase()}`}>{value}</span>
        </div>
    );
}
